import { Text, Box, VStack, Heading, HStack } from '@chakra-ui/react';
import DataTableCard from './DataTableCard';
import { useBinderCardColors } from '../hooks/useBinderCardColors';

interface ClaimsCardProps {
  data?: any;
}

const ClaimsCard = ({ data }: ClaimsCardProps) => {
  const colors = useBinderCardColors();
  const drivers = data?.drivers || [];
  const insuranceSource = data?.insuranceSource;

  // Common headers for all claims tables
  const claimsHeaders = ['Date', 'Driver', 'Type', 'Fault', 'Payout'];

  // Format empty value display
  const formatEmptyValue = (value: string) => {
    if (!value || value.trim() === '') {
      return (
        <Text color="gray.400" fontStyle="italic" fontSize="sm">
          Not provided
        </Text>
      );
    }
    return value;
  };

  // Create table rows from claims data
  const createClaimsRows = (claims: any[]) => {
    if (claims.length === 0) {
      return [];
    }

    return claims.map((claim: any) => [
      formatEmptyValue(claim.date),
      formatEmptyValue(claim.driver),
      formatEmptyValue(claim.type),
      formatEmptyValue(claim.fault),
      formatEmptyValue(claim.payout),
    ]);
  };

  // Handle case when no drivers have claims data
  if (drivers.length === 0) {
    return (
      <Box p={4} borderRadius="md" bg={colors.emptyFieldBgColor}>
        <Text color="gray.500" fontStyle="italic" textAlign="center">
          No driver claims data available
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {data.remarks && (
        <Box p={3} bg={colors.emptyFieldBgColor} borderRadius="md">
          <Text
            fontSize="xs"
            color={colors.labelColor}
            mb={2}
            fontWeight="semibold"
          >
            Remarks:
          </Text>
          <VStack align="start" spacing={2}>
            <Text fontSize="sm" color={colors.textColor}>
              {data.remarks}
            </Text>
          </VStack>
        </Box>
      )}
      {drivers.map((driver: any) => (
        <Box key={driver.driverIndex}>
          {/* Driver Header with Summary */}
          <Box mb={3}>
            <HStack align="baseline" mb={1}>
              <Heading size="sm" color={colors.textColor}>
                {driver.driverName}
              </Heading>
            </HStack>
            <HStack spacing={3}>
              <Text fontSize="sm" color={colors.descriptionColor}>
                {insuranceSource}: {driver.autoplusClaims?.length || 0} claim
                {driver.autoplusClaims?.length !== 1 ? 's' : ''}
              </Text>
              <Text fontSize="sm" color={colors.descriptionColor}>
                •
              </Text>
              <Text fontSize="sm" color={colors.descriptionColor}>
                Quote: {driver.quoteClaims?.length || 0} claim
                {driver.quoteClaims?.length !== 1 ? 's' : ''}
              </Text>
              <Text fontSize="sm" color={colors.descriptionColor}>
                •
              </Text>
              <Text fontSize="sm" color={colors.descriptionColor}>
                Other Losses: {driver.otherLosses?.length || 0}
              </Text>
            </HStack>
          </Box>

          <VStack spacing={3} align="stretch">
            {/* Claims (AutoPlus) Table */}
            {driver.autoplusClaims?.length > 0 && (
              <DataTableCard
                description={`Claims (${insuranceSource})`}
                headers={claimsHeaders}
                rows={createClaimsRows(driver.autoplusClaims)}
              />
            )}

            {/* Claims (Quote) Table */}
            {driver.quoteClaims?.length > 0 && (
              <DataTableCard
                description="Claims (Quote)"
                headers={claimsHeaders}
                rows={createClaimsRows(driver.quoteClaims)}
              />
            )}

            {/* Other Losses Table */}
            {driver.otherLosses?.length > 0 && (
              <DataTableCard
                description={`Other Losses (${insuranceSource})`}
                headers={claimsHeaders}
                rows={createClaimsRows(driver.otherLosses)}
              />
            )}

            {/* Show message if no claims */}
            {driver.autoplusClaims?.length === 0 &&
              driver.quoteClaims?.length === 0 &&
              driver.otherLosses?.length === 0 && (
                <Box p={3} bg={colors.emptyFieldBgColor} borderRadius="md">
                  <Text fontSize="sm" color="gray.500" fontStyle="italic">
                    No claims history found for this driver
                  </Text>
                </Box>
              )}
          </VStack>
        </Box>
      ))}
    </VStack>
  );
};

export default ClaimsCard;
